import type {
	Command,
	CommandInternal,
	CommandSections,
	OrderedFilteredCommands,
} from '@atlassian/jira-command-palette/src/common/types/commands/index.tsx';

/**
 * Applies commands with `alwaysShowWithSearchResults` flag to search results.
 * These commands will appear in their specified sections or as ungrouped commands
 * even if they don't match the search query.
 * Creates sections if they don't exist but are registered.
 * Returns a new grouped commands object without mutating the input.
 */
export const applyAlwaysShowWithSearchResults = (
	grouped: OrderedFilteredCommands,
	alwaysShowCommands: Command[],
	selectedSections?: CommandSections,
): OrderedFilteredCommands => {
	if (alwaysShowCommands.length === 0) {
		return grouped;
	}

	// Create new grouped result to avoid mutation
	const newGrouped = {
		...grouped,
		grouped: [...grouped.grouped],
		ungrouped: [...grouped.ungrouped],
	} satisfies OrderedFilteredCommands;

	for (const command of alwaysShowCommands) {
		// Create command internal with fixed search relevancy
		const commandInternal: CommandInternal = {
			...command,
			searchRelevancyScore: 0, // Fixed position, not affected by search relevancy
		};

		if (!command.section) {
			newGrouped.ungrouped.push({ command: commandInternal });
			newGrouped.length += 1;
			// Nothing else to do, for this command skip to the next
			// eslint-disable-next-line no-continue
			continue;
		}

		// Find existing section group
		const existingSectionGroupIndex = newGrouped.grouped.findIndex(
			(group) => group.id === command.section,
		);

		// FindIndex returns -1 when no match is found
		if (existingSectionGroupIndex >= 0) {
			// Add command to existing section
			newGrouped.grouped[existingSectionGroupIndex] = {
				...newGrouped.grouped[existingSectionGroupIndex],
				commands: [
					...newGrouped.grouped[existingSectionGroupIndex].commands,
					{ command: commandInternal },
				],
			};
			newGrouped.length += 1;
		} else if (selectedSections?.[command.section]) {
			// Create new section with the command if section is registered
			const section = selectedSections[command.section];
			const newSectionGroup = {
				...(section.renderName ? { renderGroup: section.renderName } : { group: section.name }),
				id: command.section,
				priority: section.priority,
				commands: [{ command: commandInternal }],
			};
			newGrouped.grouped.push(newSectionGroup);
			newGrouped.length += 1;
		}
	}

	return newGrouped;
};
